import React, { useCallback, useMemo } from 'react';
import { useParams } from "react-router-dom";
import Box from '@mui/material/Box';
import Typography from '@mui/joy/Typography';
import Button from "@mui/joy/Button";
import Divider from "@mui/joy/Divider";
import Stack from '@mui/joy/Stack';
import Card from '@mui/joy/Card';
import Grid from '@mui/joy/Grid';
import Alert from '@mui/joy/Alert';
import WarningIcon from '@mui/icons-material/Warning';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { NotificationService } from "../../../common/notification";
import CircularProgress from "@mui/joy/CircularProgress";
import { AdminUserPenaltyInfo, AdminUserPenaltyHistory, GetPenaltyHistoryRequest } from "../../../models/user";
import { userPenaltyInfoApi, userPenaltyUnbanApi, userPenaltyHistoryApi } from "../usersApi";
import { formatWith } from "../../../common/dateTime";
import Table from '@mui/joy/Table';
import Sheet from '@mui/joy/Sheet';
import Pagination from '@mui/material/Pagination';
import Chip from '@mui/joy/Chip';
import Link from '@mui/joy/Link';

// Константы
const ITEMS_PER_PAGE = 20;

// Утилиты
const formatRemainingTime = (endTime: Date): string => {
    const now = new Date();
    const diff = endTime.getTime() - now.getTime();
    
    if (diff <= 0) return 'закончился';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    const parts: string[] = [];
    
    if (days > 0) {
        const dayText = days === 1 ? 'день' : days < 5 ? 'дня' : 'дней';
        parts.push(`${days} ${dayText}`);
    }
    
    if (hours > 0) {
        const hourText = hours === 1 ? 'час' : hours < 5 ? 'часа' : 'часов';
        parts.push(`${hours} ${hourText}`);
    }
    
    if (minutes > 0) {
        const minuteText = minutes === 1 ? 'минута' : minutes < 5 ? 'минуты' : 'минут';
        parts.push(`${minutes} ${minuteText}`);
    }
    
    return parts.join(' ');
};

// Компоненты
const PenaltyInfoCard = React.memo(({ 
    penaltyInfo,
    onRefresh,
    refreshing
}: { 
    penaltyInfo: AdminUserPenaltyInfo;
    onRefresh: () => void;
    refreshing: boolean;
}) => (
    <Card>
        <Box sx={{ mb: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography level="title-lg">Информация о штрафах и блокировках пользователя</Typography>
                <Button size="sm" variant="outlined" onClick={onRefresh} disabled={refreshing}>
                    Обновить
                </Button>
            </Stack>
        </Box>
        <Divider sx={{ mb: 2 }} />
        
        <Grid container spacing={3}>
            <Grid xs={6}>
                <Stack spacing={2}>
                    <Box>
                        <Typography level="title-sm" variant="soft">
                            Всего штрафных баллов за все время
                        </Typography>
                        <Typography level="h3">
                            {penaltyInfo.totalPenaltyPoints}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography level="title-sm" variant="soft">
                            Количество активных штрафных баллов
                        </Typography>
                        <Typography level="h4">
                            {penaltyInfo.activePenaltyPoints}
                        </Typography>
                    </Box>
                </Stack>
            </Grid>
            <Grid xs={6}>
                <Stack spacing={2}>
                    <Box>
                        <Typography level="title-sm" variant="soft">
                            Статус торговли
                        </Typography>
                        {penaltyInfo.isTradingBlocked ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <BlockIcon color="error" />
                                <Typography level="h4">Заблокирована</Typography>
                            </Stack>
                        ) : (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CheckCircleIcon color="success" />
                                <Typography level="h4">Разрешена</Typography>
                            </Stack>
                        )}
                    </Box>
                </Stack>
            </Grid>
        </Grid>
    </Card>
));

const ActiveBanAlert = React.memo(({ 
    remainingBanTime, 
    exactBanTime, 
    onUnban, 
    loading 
}: {
    remainingBanTime: string;
    exactBanTime: string;
    onUnban: () => void;
    loading: boolean;
}) => (
    <Box sx={{ 
        mt: 3, 
        p: 2, 
        bgcolor: 'danger.50', 
        borderRadius: 1, 
        border: '1px solid', 
        borderColor: 'danger.200' 
    }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
                <Typography level="title-sm" sx={{ mb: 1 }}>
                    <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Активная блокировка торговли
                </Typography>
                <Typography level="body-sm">
                    Блокировка истекает {remainingBanTime} ({exactBanTime})
                </Typography>
            </Box>
            <Button
                color="danger"
                variant="solid"
                onClick={onUnban}
                disabled={loading}
            >
                Снять бан
            </Button>
        </Stack>
    </Box>
));

const PenaltyHistoryTable = React.memo(({ 
    penaltyHistory, 
    historyLoading, 
    totalPages, 
    currentPage, 
    onPageChange 
}: {
    penaltyHistory: AdminUserPenaltyHistory[];
    historyLoading: boolean;
    totalPages: number;
    currentPage: number;
    onPageChange: (event: React.ChangeEvent<unknown>, page: number) => void;
}) => (
    <Card sx={{ mt: 3 }}>
        <Box sx={{ mb: 2 }}>
            <Typography level="title-lg">История штрафных балов</Typography>
            <Typography level="body-sm" variant="soft">
                Детальная информация о всех начисленных штрафах
            </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        
        {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        ) : (
            <>
                <Sheet sx={{ height: 400, overflow: 'auto' }}>
                    <Table stickyHeader>
                        <thead>
                            <tr>
                                <th>Дата</th>
                                <th>Баллы</th>
                                <th>Описание</th>
                                <th>Заказ</th>
                            </tr>
                        </thead>
                        <tbody>
                            {penaltyHistory.map((item) => (
                                <tr key={item.id}>
                                    <td>{formatWith(item.created, 'DD.MM.YYYY HH:mm')}</td>
                                    <td>
                                        <Chip color="warning" variant="soft" size="sm">
                                            {item.points}
                                        </Chip>
                                    </td>
                                    <td>{item.description || '-'}</td>
                                    <td>
                                        {item.orderId ? (
                                            <Link href={`/app/orders/${item.orderId}`} target="_blank">
                                                #{item.orderId}
                                            </Link>
                                        ) : '-'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Sheet>
                
                {totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={onPageChange}
                            showFirstButton
                            showLastButton
                        />
                    </Box>
                )}
            </>
        )}
    </Card>
));

const MobilePenaltyInfo = React.memo(({ 
    penaltyInfo,
    onRefresh,
    refreshing
}: { 
    penaltyInfo: AdminUserPenaltyInfo;
    onRefresh: () => void;
    refreshing: boolean;
}) => (
    <Stack spacing={2}>
        <Card>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography level="title-md">
                    Штрафные баллы
                </Typography>
                <Button size="sm" variant="outlined" onClick={onRefresh} disabled={refreshing}>
                    Обновить
                </Button>
            </Stack>
            <Stack spacing={2}>
                <Box>
                    <Typography level="body-sm" variant="soft">
                        Всего штрафных баллов за все время
                    </Typography>
                    <Typography level="h3">
                        {penaltyInfo.totalPenaltyPoints}
                    </Typography>
                </Box>
                <Box>
                    <Typography level="body-sm" variant="soft">
                        Количество активных штрафных баллов
                    </Typography>
                    <Typography level="h4">
                        {penaltyInfo.activePenaltyPoints}
                    </Typography>
                </Box>
            </Stack>
        </Card>

        <Card>
            <Typography level="title-md" sx={{ mb: 2 }}>
                Блокировки
            </Typography>
            <Stack spacing={2}>
                <Box>
                    <Typography level="body-sm" variant="soft">
                        Статус торговли
                    </Typography>
                    {penaltyInfo.isTradingBlocked ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <BlockIcon color="error" />
                            <Typography level="h4">Заблокирована</Typography>
                        </Stack>
                    ) : (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <CheckCircleIcon color="success" />
                            <Typography level="h4">Разрешена</Typography>
                        </Stack>
                    )}
                </Box>
            </Stack>
        </Card>
    </Stack>
));

const MobileActiveBanCard = React.memo(({ 
    remainingBanTime, 
    exactBanTime, 
    onUnban, 
    loading 
}: {
    remainingBanTime: string;
    exactBanTime: string;
    onUnban: () => void;
    loading: boolean;
}) => (
    <Card sx={{ 
        bgcolor: 'danger.50', 
        border: '1px solid', 
        borderColor: 'danger.200' 
    }}>
        <Typography level="title-sm" sx={{ mb: 2 }}>
            <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Активная блокировка торговли
        </Typography>
        <Typography level="body-sm" sx={{ mb: 2 }}>
            Блокировка истекает {remainingBanTime} ({exactBanTime})
        </Typography>
        <Button
            color="danger"
            variant="solid"
            fullWidth
            onClick={onUnban}
            disabled={loading}
        >
            Снять бан
        </Button>
    </Card>
));

const MobilePenaltyHistory = React.memo(({ 
    penaltyHistory, 
    historyLoading, 
    totalPages, 
    currentPage, 
    onPageChange 
}: {
    penaltyHistory: AdminUserPenaltyHistory[];
    historyLoading: boolean;
    totalPages: number;
    currentPage: number;
    onPageChange: (event: React.ChangeEvent<unknown>, page: number) => void;
}) => (
    <Card>
        <Typography level="title-md" sx={{ mb: 2 }}>
            История штрафных балов
        </Typography>
        
        {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        ) : (
            <Stack spacing={2}>
                {penaltyHistory.map((item) => (
                    <Box key={item.id} sx={{ 
                        p: 2, 
                        border: '1px solid', 
                        borderColor: 'divider', 
                        borderRadius: 1 
                    }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                            <Typography level="body-sm" variant="soft">
                                {formatWith(item.created, 'DD.MM.YYYY HH:mm')}
                            </Typography>
                            <Chip color="warning" variant="soft" size="sm">
                                {item.points} баллов
                            </Chip>
                        </Stack>
                        {item.description && (
                            <Typography level="body-sm" sx={{ mb: 1 }}>
                                {item.description}
                            </Typography>
                        )}
                        {item.orderId && (
                            <Link href={`/app/orders/${item.orderId}`} target="_blank" level="body-sm">
                                Заказ #{item.orderId}
                            </Link>
                        )}
                    </Box>
                ))}
                
                {totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={onPageChange}
                            size="small"
                        />
                    </Box>
                )}
            </Stack>
        )}
    </Card>
));

export function UserPenaltyInfo() {

    const { id } = useParams();
    const [penaltyInfo, setPenaltyInfo] = React.useState<AdminUserPenaltyInfo | undefined>();
    const [penaltyHistory, setPenaltyHistory] = React.useState<AdminUserPenaltyHistory[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [historyLoading, setHistoryLoading] = React.useState(false);
    const [currentPage, setCurrentPage] = React.useState(1);
    const [totalPages, setTotalPages] = React.useState(1);
    const [totalItems, setTotalItems] = React.useState(0);

    // Мемоизированные значения
    const userId = useMemo(() => id && BigInt(id) > 0 ? BigInt(id) : null, [id]);
    const hasActiveBan = useMemo(() => 
        penaltyInfo?.tradingBlockUntil && new Date(penaltyInfo.tradingBlockUntil) > new Date(), 
        [penaltyInfo?.tradingBlockUntil]
    );
    const remainingBanTime = useMemo(() => 
        penaltyInfo?.tradingBlockUntil ? formatRemainingTime(new Date(penaltyInfo.tradingBlockUntil)) : null, 
        [penaltyInfo?.tradingBlockUntil]
    );
    const exactBanTime = useMemo(() => 
        penaltyInfo?.tradingBlockUntil ? formatWith(penaltyInfo.tradingBlockUntil, 'DD.MM.YYYY в HH:mm') : null, 
        [penaltyInfo?.tradingBlockUntil]
    );

    // Callback функции
    const loadPenaltyInfo = useCallback(async (userId: bigint) => {
        try {
            setLoading(true);
            const result = await userPenaltyInfoApi(userId);
            if (result.result) {
                setPenaltyInfo(result.value);
            } else {
                NotificationService.alertError("Не удалось загрузить информацию о штрафах. Пожалуйста попробуйте позже или обратитесь в службу поддержки");
            }
        } finally {
            setLoading(false);
        }
    }, []);

    const loadPenaltyHistory = useCallback(async (userId: bigint) => {
        try {
            setHistoryLoading(true);
            const request: GetPenaltyHistoryRequest = {
                page: currentPage,
                count: ITEMS_PER_PAGE
            };
            const result = await userPenaltyHistoryApi(userId, request);
            if (result.result && result.value) {
                setPenaltyHistory(result.value.items);
                setTotalItems(result.value.total);
                setTotalPages(Math.ceil(result.value.total / ITEMS_PER_PAGE));
            } else {
                NotificationService.alertError("Не удалось загрузить историю штрафов. Пожалуйста попробуйте позже или обратитесь в службу поддержки");
            }
        } finally {
            setHistoryLoading(false);
        }
    }, [currentPage]);

    const loadUserData = useCallback(async (userId: bigint) => {
        await Promise.all([
            loadPenaltyInfo(userId),
            loadPenaltyHistory(userId)
        ]);
    }, [loadPenaltyInfo, loadPenaltyHistory]);

    const handleUnban = useCallback(async () => {
        if (!userId) return;
        
        setLoading(true);
        try {
            const result = await userPenaltyUnbanApi(userId);
            if (result.result) {
                NotificationService.alertSuccess("Бан успешно снят.");
                await loadUserData(userId);
            } else {
                NotificationService.alertError("Не удалось снять бан. Пожалуйста попробуйте позже или обратитесь в службу поддержки.");
            }
        } finally {
            setLoading(false);
        }
    }, [userId, loadUserData]);

    const handlePageChange = useCallback((event: React.ChangeEvent<unknown>, page: number) => {
        setCurrentPage(page);
    }, []);

    const handleRefresh = useCallback(async () => {
        if (!userId) return;
        await loadUserData(userId);
    }, [userId, loadUserData]);

    // Эффекты
    React.useEffect(() => {
        if (userId) {
            loadUserData(userId).catch(console.error);
        }
    }, [userId, loadUserData]);

    // Рендер
    if (loading) {
        return <CircularProgress />;
    }

    if (!penaltyInfo) {
        return (
            <Alert color="danger" variant="soft">
                <Typography level="body-sm">
                    Не удалось загрузить информацию о штрафах
                </Typography>
            </Alert>
        );
    }

    return (
        <>
            {/* Desktop Version */}
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <PenaltyInfoCard 
                    penaltyInfo={penaltyInfo} 
                    onRefresh={handleRefresh}
                    refreshing={loading || historyLoading}
                />
                
                {hasActiveBan && (
                    <ActiveBanAlert
                        remainingBanTime={remainingBanTime!}
                        exactBanTime={exactBanTime!}
                        onUnban={handleUnban}
                        loading={loading}
                    />
                )}

                <PenaltyHistoryTable
                    penaltyHistory={penaltyHistory}
                    historyLoading={historyLoading}
                    totalPages={totalPages}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                />
            </Box>

            {/* Mobile Version */}
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                <MobilePenaltyInfo 
                    penaltyInfo={penaltyInfo}
                    onRefresh={handleRefresh}
                    refreshing={loading || historyLoading}
                />
                
                {hasActiveBan && (
                    <MobileActiveBanCard
                        remainingBanTime={remainingBanTime!}
                        exactBanTime={exactBanTime!}
                        onUnban={handleUnban}
                        loading={loading}
                    />
                )}

                <MobilePenaltyHistory
                    penaltyHistory={penaltyHistory}
                    historyLoading={historyLoading}
                    totalPages={totalPages}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                />
            </Box>
        </>
    );
}
